import React from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/lib/appApi";
import { ArrowLeft } from "lucide-react";

const UPDATED = "2026";

export default function Privacy({ asOverlay = false, onClose }) {
  const navigate = useNavigate();
  const close = () => (asOverlay ? onClose && onClose() : navigate(-1));
  return (
    <div className={asOverlay ? "fixed inset-0 z-50 bg-background overflow-y-auto" : "min-h-screen bg-background text-foreground"}>
      <header className="flex items-center justify-between px-6 py-4 border-b border-border sticky top-0 glass z-10">
        <button onClick={close} data-testid="legal-back-btn" className="flex items-center gap-2 text-muted-foreground hover:text-foreground">
          <ArrowLeft className="w-4 h-4" /> {asOverlay ? "Close" : "Back"}
        </button>
        <div className="flex items-center gap-2">
          <img src={BRAND.logo} alt="logo" className="w-8 h-8 rounded-full" />
          <span className="font-heading font-extrabold">Random Cam <span className="brand-gradient-text">Chat</span></span>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-10 text-sm leading-relaxed text-muted-foreground">
        <div>
          <h1 className="font-heading text-4xl font-extrabold text-foreground">Privacy Policy</h1>
          <p className="mt-2">Last updated: {UPDATED}. Random Cam Chat is committed to protecting your privacy and complies with applicable privacy and data-protection laws worldwide to safeguard your information and your experience.</p>
        </div>

        <Section title="1. Who we are">
          Random Cam Chat ("we", "us") provides an adults-only (18+/21+ depending on your location) random video and text chat service. Our privacy contact: <span className="text-foreground">randomcamchatonline@protonmail.com</span>.
        </Section>

        <Section title="2. What we collect and why (data minimization)">
          We deliberately collect as little as possible. We store only:
          <ul className="list-disc pl-6 mt-2 space-y-1">
            <li><strong className="text-foreground">Email address and display name</strong> — obtained from Google Sign-In, used solely to create and operate your account (login, support, safety).</li>
            <li><strong className="text-foreground">Approximate country</strong> — derived from your IP address for age-gating and optional matching filters. You can hide it from other users.</li>
            <li><strong className="text-foreground">IP address</strong> — used for security, abuse prevention and enforcing bans.</li>
            <li><strong className="text-foreground">Text chat logs</strong> — retained only for safety/moderation and automatically deleted per the administrator's retention schedule.</li>
            <li><strong className="text-foreground">Subscription/payment records</strong> — payment is processed by Stripe; we retain the plan, status and the email used for accounting and tax compliance. We never store your card details.</li>
          </ul>
          <p className="mt-2"><strong className="text-foreground">Video and audio are peer-to-peer and are not recorded or stored by us.</strong></p>
        </Section>

        <Section title="3. Consent">
          By confirming the age gate and signing in, you consent to this processing. You may withdraw consent at any time by deleting your account (subject to the limited retention described below).
        </Section>

        <Section title="4. Your rights">
          You have the right to: access the personal information we hold about you; request correction; withdraw consent; and request deletion. Use <button onClick={() => navigate("/profile")} className="text-primary underline">Profile &amp; settings</button> to delete your account, or email us.
        </Section>

        <Section title="5. Account deletion & limited retention">
          Deleting your account removes your login and profile, and your email becomes available for future re-registration. For legal compliance, fraud prevention and safety we retain a minimal record: your email and name, any abuse reports involving you, and payment history. If your account was banned, the ban (email and/or IP) is retained so a new account cannot be created to evade it.
        </Section>

        <Section title="6. Sharing & third parties">
          We do not sell your personal information. We share data only with service providers that help us operate (e.g., <strong className="text-foreground">Stripe</strong> for payments, <strong className="text-foreground">Google</strong> for sign-in) under their own privacy terms.
        </Section>

        <Section title="7. Security & retention">
          We use encryption in transit and access controls. Chat logs are auto-deleted on the schedule chosen by the administrator (daily/weekly/monthly/yearly). We keep other data only as long as necessary for the purposes above or as required by law.
        </Section>

        <Section title="8. Safety, monitoring & moderation">
          To protect members, administrators may review reports, text chat logs, and — for active abuse investigations — a live view of a user's camera. A visible notice and this policy inform you of this. Recording/screenshotting other members is prohibited.
        </Section>

        <Section title="9. Contact & complaints">
          Questions or complaints: <span className="text-foreground">randomcamchatonline@protonmail.com</span>. We aim to respond to all privacy requests promptly.
        </Section>

        <hr className="border-border" />

        <div id="terms">
          <h1 className="font-heading text-4xl font-extrabold text-foreground">Terms &amp; Conditions</h1>
          <p className="mt-2">Last updated: {UPDATED}.</p>
        </div>

        <Section title="1. Eligibility (18+/21+)">
          You must be a legal adult in your jurisdiction (18, or 21 where required) to use this service. The service contains adult content. By using it you confirm you meet the age requirement.
        </Section>

        <Section title="2. Acceptable use">
          You agree not to: broadcast content involving minors or any illegal content; harass, threaten or abuse others; record, screenshot or redistribute other members' video, audio or images; impersonate others; or spam/advertise. Violations lead to immediate banning by email and IP.
        </Section>

        <Section title="3. Subscriptions, billing & cancellation">
          Premium subscriptions (monthly, 6-month, yearly) are billed via Stripe. You may cancel anytime from Profile &amp; settings; cancellation takes effect at the end of the current paid period and no partial refunds are provided. Prices are shown at checkout.
        </Section>

        <Section title="4. Moderation & termination">
          We may suspend or ban accounts that violate these terms. Banned emails and IP addresses are retained to prevent re-registration.
        </Section>

        <Section title="5. Disclaimer & liability">
          The service is provided "as is". You interact with strangers at your own risk. To the maximum extent permitted by law, we are not liable for user conduct or content.
        </Section>

        <Section title="6. Changes">
          We may update these terms; continued use constitutes acceptance. Material changes will be posted here.
        </Section>

        <div className="pt-6">
          <Button onClick={close} className="brand-gradient-bg text-white border-0 rounded-full">{asOverlay ? "Close" : "Back to app"}</Button>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-heading text-lg font-bold text-foreground mb-2">{title}</h2>
      <div>{children}</div>
    </section>
  );
}
